import type { NextApiRequest, NextApiResponse } from 'next';
import { getSupabaseAdminClient } from '@/lib/supabaseAdmin';
import { archiveFinancialDataFromInvoice } from '@/lib/supabaseService';
import { PDFParse } from 'pdf-parse';

interface VerifyFinancialRequest {
  documentoId: string;
  forceReextract?: boolean;
}

interface LineaIVA {
  tipo: number;
  base: number;
  cuota: number;
  cuenta?: string;
}

interface DatosFacturaIA {
  fechaFactura?: string;
  numeroFactura?: string;
  nifEmisor?: string;
  razonSocialEmisor?: string;
  nifReceptor?: string;
  razonSocialReceptor?: string;
  baseImponible?: number;
  tipoIVA?: number;
  cuotaIVA?: number;
  totalFactura?: number;
  nivelConfianza: number;
  tipoDocumento?: string;
  actividad?: string;
  fechaContable?: string;
  fechaVencimiento?: string;
  porcentajeRetencion?: number;
  importeRetencion?: number;
  tipoRetencion?: string;
  lineasIVA?: LineaIVA[];
  suplidos?: number;
  cuentaProveedor?: string;
  cuentasGasto?: string[];
  metodoPago?: string;
}

export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  const { documentoId, forceReextract = false }: VerifyFinancialRequest = req.body;

  if (!documentoId) {
    return res.status(400).json({ error: 'Document ID is required' });
  }

  try {
    const supabaseAdmin = getSupabaseAdminClient();

    // Get document details
    const { data: documento, error: docError } = await supabaseAdmin
      .from('documentos')
      .select('*')
      .eq('id', documentoId)
      .single();

    if (docError || !documento) {
      console.error('[verify-financial] Document not found:', docError);
      return res.status(404).json({ error: 'Document not found' });
    }

    // If document already has AI data and we're not forcing re-extraction, return existing data
    if (!forceReextract && documento.datos_ia && documento.procesado_ia) {
      return res.status(200).json({ 
        datosIA: documento.datos_ia,
        message: 'Using existing AI-extracted data'
      });
    }

    const openaiApiKey = process.env.OPENAI_API_KEY;
    let datosIA: DatosFacturaIA;

    if (!openaiApiKey) {
      return res.status(500).json({ error: 'OpenAI API key not configured. Cannot extract financial data.' });
    } else {
      // Real OpenAI extraction — download file and send as base64
      try {
        datosIA = await extractWithOpenAI(supabaseAdmin, documento, openaiApiKey);
      } catch (error: any) {
        console.error('[verify-financial] OpenAI extraction failed:', error.message);
        throw new Error(`Error en extraccion IA: ${error.message}`);
      }
    }

    // Update document with extracted data (include aliases for downstream compatibility)
    const datosIAConAliases: any = {
      ...datosIA,
      importeTotal: (datosIA as any).totalFactura ?? datosIA.baseImponible ?? null,
      fecha: datosIA.fechaFactura || null,
    };

    const { error: updateError } = await supabaseAdmin
      .from('documentos')
      .update({
        datos_ia: datosIAConAliases,
        procesado_ia: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', documentoId);

    if (updateError) {
      console.error('[verify-financial] Error updating document:', updateError);
      return res.status(500).json({ error: 'Error updating document with extracted data' });
    }

    // Archive financial data to datos_financieros
    const totalFactura =
      (datosIA as any).importeTotal ??
      datosIA.totalFactura ??
      datosIA.baseImponible ??
      0;
    if (totalFactura) {
      try {
        await archiveFinancialDataFromInvoice(documento.cliente_id, {
          tipo: documento.tipo,
          datos_ia: { ...datosIA, totalFactura, importeTotal: totalFactura },
          fecha_subida: documento.fecha_subida
        }, supabaseAdmin);
      } catch (archiveError) {
        console.error('[verify-financial] Error archiving financial data (non-critical):', archiveError);
      }
    }

    return res.status(200).json({ 
      datosIA: datosIAConAliases,
      message: 'Financial data extracted successfully'
    });

  } catch (error: any) {
    console.error('[verify-financial] Error:', error);
    return res.status(500).json({ error: error.message || 'Error processing document' });
  }
}

/**
 * Extract financial data using OpenAI GPT-4o Vision.
 * Downloads the actual file from Supabase Storage and sends as base64.
 */
async function extractWithOpenAI(
  supabaseAdmin: any,
  documento: any,
  openaiApiKey: string
): Promise<DatosFacturaIA> {
  const documentoUrl = documento.url_archivo;
  
  if (!documentoUrl) {
    throw new Error('Document has no file URL');
  }

  // Download the file from Supabase Storage
  const storagePath = documentoUrl.replace(
    /^https:\/\/[^/]+\/storage\/v1\/object\/public\/documentos\//,
    ''
  );
  
  const { data: fileData, error: fileError } = await supabaseAdmin.storage
    .from('documentos')
    .download(storagePath);

  if (fileError || !fileData) {
    console.error('[verify-financial] Could not download file:', fileError);
    throw new Error('Could not download document file');
  }

  const buffer = Buffer.from(await fileData.arrayBuffer());
  const mimeType = fileData.type || 'application/pdf';
  const isPdf = mimeType === 'application/pdf' || documento.nombre?.toLowerCase().endsWith('.pdf');
  const isImage = mimeType.startsWith('image/');

  const systemPrompt = 'Eres un experto en extracción de datos de facturas españolas. Extrae la información financiera de manera precisa y estructurada. Responde SIEMPRE en formato JSON válido.';
  
  const extractionInstructions = `Extrae la siguiente información del documento y responde en formato JSON estricto:

{
  "tipoDocumento": "factura_proveedor|factura_cliente|factura_rectificativa|recibo|nomina|otro",
  "actividad": "FACTURA|ABONO|RECTIFICATIVA|otro",
  "numeroFactura": "número de factura, o null",
  "fechaFactura": "YYYY-MM-DD, o null",
  "fechaContable": "YYYY-MM-DD fecha contable si aparece, o misma que fechaFactura, o null",
  "fechaVencimiento": "YYYY-MM-DD fecha de vencimiento si aparece, o null",
  "nifEmisor": "CIF/NIF del emisor",
  "razonSocialEmisor": "Razón social o nombre del emisor",
  "nifReceptor": "CIF/NIF del receptor si está disponible, o null",
  "razonSocialReceptor": "Razón social del receptor si está disponible, o null",
  "baseImponible": 0.00,
  "tipoIVA": 21.0,
  "cuotaIVA": 0.00,
  "lineasIVA": [
    {"tipo": 21, "base": 0.00, "cuota": 0.00, "cuenta": "código de cuenta contable o null"}
  ],
  "porcentajeRetencion": "porcentaje de retención (ej: 15 para IRPF 15%), o null",
  "importeRetencion": 0.00,
  "tipoRetencion": "IRPF|IS|otro, o null",
  "suplidos": 0.00,
  "totalFactura": 0.00,
  "cuentaProveedor": "código cuenta proveedor si se puede inferir, o null",
  "cuentasGasto": ["código cuenta gasto, o null"],
  "metodoPago": "Domiciliación|Transferencia|Efectivo|Pagaré|Recibo|otro, o null",
  "nivelConfianza": 0.95
}

Instrucciones:
- Si algún campo no es visible o legible, usa null
- Verifica que baseImponible + cuotaIVA - importeRetencion + suplidos ≈ totalFactura
- Para documentos españoles, el IVA típico es 21%, 10% o 4%
- Si hay múltiples líneas de IVA, incluye cada una en el array lineasIVA
- Si solo hay un tipo de IVA, incluye una sola línea en lineasIVA
- Para retenciones (IRPF), extrae tanto el porcentaje como el importe
- nivelConfianza: 0.95+ = muy alto, 0.80-0.94 = alto, 0.60-0.79 = medio, <0.60 = bajo
- tipoDocumento: usa 'factura_proveedor' si es una factura de compra, 'factura_cliente' si es de venta
- Responde SOLO con JSON válido`;

  let openaiResponse: Response;

  if (isImage) {
    // For images: use GPT-4o Vision directly
    const base64 = buffer.toString('base64');
    openaiResponse = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${openaiApiKey}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: [
          { role: 'system', content: systemPrompt },
          {
            role: 'user',
            content: [
              { type: 'text', text: extractionInstructions },
              {
                type: 'image_url',
                image_url: {
                  url: `data:${mimeType};base64,${base64}`,
                  detail: 'high',
                },
              },
            ],
          },
        ],
        temperature: 0.1,
        max_tokens: 2500,
        response_format: { type: 'json_object' },
      }),
    });
  } else if (isPdf) {
    // Try extracting text from PDF first
    let pdfText = '';
    try {
      const pdf = new PDFParse({ data: buffer });
      const textResult = await pdf.getText();
      pdfText = textResult.text || '';
      await pdf.destroy();
    } catch (parseErr) {
      console.warn('[verify-financial] PDFParse failed, will use Vision fallback:', parseErr);
    }

    // Strip page markers and whitespace to check for real content
    const cleanedText = pdfText.replace(/--\s*\d+\s+of\s+\d+\s*--/gi, '').trim();
    console.log('[verify-financial] PDF text: raw length:', pdfText.length, ', cleaned length:', cleanedText.length);

    if (cleanedText.length >= 50) {
      // Enough text extracted — use text-based extraction
      console.log('[verify-financial] Using text-based extraction (enough text found)');
      openaiResponse = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${openaiApiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [
            { role: 'system', content: systemPrompt },
            {
              role: 'user',
              content: `A continuación se encuentra el texto extraído de un documento PDF (factura española). Analízalo y extrae los datos financieros.\n\n--- TEXTO DEL DOCUMENTO ---\n${cleanedText}\n--- FIN DEL TEXTO ---\n\n${extractionInstructions}`,
            },
          ],
          temperature: 0.1,
          max_tokens: 2500,
          response_format: { type: 'json_object' },
        }),
      });
    } else {
      // Scanned/image PDF — use OpenAI Responses API which supports native PDF input
      console.log('[verify-financial] Using Responses API fallback (PDF appears to be scanned/image-based)');
      const base64Pdf = buffer.toString('base64');
      const responsesApiResponse = await fetch('https://api.openai.com/v1/responses', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${openaiApiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          input: [
            {
              role: 'developer',
              content: [
                { type: 'input_text', text: systemPrompt },
              ],
            },
            {
              role: 'user',
              content: [
                {
                  type: 'input_file',
                  filename: documento.nombre || 'document.pdf',
                  file_data: `data:application/pdf;base64,${base64Pdf}`,
                },
                {
                  type: 'input_text',
                  text: `Este es un PDF escaneado de una factura española. Examina el documento y extrae los datos financieros.\n\n${extractionInstructions}`,
                },
              ],
            },
          ],
          temperature: 0.1,
          max_output_tokens: 2500,
          text: { format: { type: 'json_object' } },
        }),
      });

      if (!responsesApiResponse.ok) {
        const errorText = await responsesApiResponse.text();
        console.error('[verify-financial] OpenAI Responses API error:', responsesApiResponse.status, errorText);
        throw new Error(`OpenAI Responses API error: ${responsesApiResponse.status}`);
      }

      const responsesData = await responsesApiResponse.json();
      // Responses API returns output_text directly
      const responsesContent = responsesData.output_text || responsesData.output?.[0]?.content?.[0]?.text || '{}';
      console.log('[verify-financial] Responses API output (first 200):', responsesContent.substring(0, 200));

      let extractedData: any;
      try {
        extractedData = JSON.parse(responsesContent);
      } catch {
        const jsonMatch = responsesContent.match(/\{[\s\S]*\}/);
        extractedData = jsonMatch ? JSON.parse(jsonMatch[0]) : {};
      }
      return mapToDatosFacturaIA(extractedData, documento);
    }
  } else {
    // For other types: send metadata-based prompt as fallback
    openaiResponse = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${openaiApiKey}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: [
          { role: 'system', content: systemPrompt },
          {
            role: 'user',
            content: `Documento: ${documento.nombre} (tipo: ${documento.tipo})\n\n${extractionInstructions}`,
          },
        ],
        temperature: 0.1,
        max_tokens: 2500,
        response_format: { type: 'json_object' },
      }),
    });
  }

  if (!openaiResponse.ok) {
    const errorText = await openaiResponse.text();
    console.error('[verify-financial] OpenAI API error:', openaiResponse.status, errorText);
    throw new Error(`OpenAI API error: ${openaiResponse.status}`);
  }

  const openaiData = await openaiResponse.json();
  const content = openaiData.choices?.[0]?.message?.content || '{}';
  
  let extractedData: any;
  try {
    extractedData = JSON.parse(content);
  } catch {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    extractedData = jsonMatch ? JSON.parse(jsonMatch[0]) : {};
  }

  return mapToDatosFacturaIA(extractedData, documento);
}

function mapToDatosFacturaIA(extractedData: any, documento: any): DatosFacturaIA {
  // Map lineasIVA array
  let lineasIVA: LineaIVA[] | undefined;
  if (Array.isArray(extractedData.lineasIVA)) {
    lineasIVA = extractedData.lineasIVA.map((linea: any) => ({
      tipo: parseFloat(linea.tipo) || 0,
      base: parseFloat(linea.base) || 0,
      cuota: parseFloat(linea.cuota) || 0,
      cuenta: linea.cuenta || undefined,
    }));
  }

  return {
    fechaFactura: extractedData.fechaFactura || null,
    numeroFactura: extractedData.numeroFactura || null,
    nifEmisor: extractedData.nifEmisor || null,
    razonSocialEmisor: extractedData.razonSocialEmisor || null,
    nifReceptor: extractedData.nifReceptor || null,
    razonSocialReceptor: extractedData.razonSocialReceptor || null,
    baseImponible: extractedData.baseImponible != null ? parseFloat(extractedData.baseImponible) : undefined,
    tipoIVA: extractedData.tipoIVA != null ? parseFloat(extractedData.tipoIVA) : undefined,
    cuotaIVA: extractedData.cuotaIVA != null ? parseFloat(extractedData.cuotaIVA) : undefined,
    totalFactura: extractedData.totalFactura != null ? parseFloat(extractedData.totalFactura) : undefined,
    nivelConfianza: extractedData.nivelConfianza ? parseFloat(extractedData.nivelConfianza) : 0.5,
    tipoDocumento: extractedData.tipoDocumento || documento.tipo,
    actividad: extractedData.actividad || undefined,
    fechaContable: extractedData.fechaContable || undefined,
    fechaVencimiento: extractedData.fechaVencimiento || undefined,
    porcentajeRetencion: extractedData.porcentajeRetencion != null ? parseFloat(extractedData.porcentajeRetencion) : undefined,
    importeRetencion: extractedData.importeRetencion != null ? parseFloat(extractedData.importeRetencion) : undefined,
    tipoRetencion: extractedData.tipoRetencion || undefined,
    lineasIVA,
    suplidos: extractedData.suplidos != null ? parseFloat(extractedData.suplidos) : undefined,
    cuentaProveedor: extractedData.cuentaProveedor || undefined,
    cuentasGasto: Array.isArray(extractedData.cuentasGasto) ? extractedData.cuentasGasto.filter(Boolean) : undefined,
    metodoPago: extractedData.metodoPago || undefined,
  };
}

